import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AlertTriangle, ExternalLink } from 'lucide-react'
import { authClient } from '../../../lib/auth-client'
import { authorizeUrl, getOAuthQuery } from '../../../lib/oauth'

type PublicClient = { client_id: string; client_name?: string; client_uri?: string }

// Plain-language descriptions of the scopes an MCP client can request.
const scopeDescriptions: Record<string, string> = {
    openid: 'Confirm who you are',
    profile: 'See your name',
    email: 'See your email address',
    offline_access: 'Stay connected without asking you to sign in again',
}

const roleDescriptions: Record<string, string> = {
    systemAdmin: 'as a system administrator, across every business',
    businessAdmin: 'as a business administrator, limited to your business and its sub-businesses',
    user: 'as a standard user, limited to your business',
}

export const OAuthConsentRoute = () => {
    const [searchParams] = useSearchParams()
    const oauthQuery = getOAuthQuery()
    const { data: session, isPending } = authClient.useSession()
    const [client, setClient] = useState<PublicClient | null>(null)
    const [submitting, setSubmitting] = useState<'accept' | 'deny' | null>(null)

    const clientId = searchParams.get('client_id')
    const scopes = (searchParams.get('scope') ?? '').split(' ').filter(Boolean)
    // The client's name is self-declared at registration; where the code is sent is what identifies it.
    const redirectHost = (() => {
        try {
            return new URL(searchParams.get('redirect_uri') ?? '').host
        } catch {
            return null
        }
    })()

    useEffect(() => {
        // Without a session the consent call cannot succeed; let the authorize endpoint send
        // the user through login and back here.
        if (!isPending && !session?.user && oauthQuery) {
            window.location.href = authorizeUrl(oauthQuery)
        }
    }, [isPending, session, oauthQuery])

    useEffect(() => {
        if (!clientId || !session?.user) return
        authClient
            .$fetch<PublicClient>('/oauth2/public-client', { query: { client_id: clientId } })
            .then(({ data }) => setClient(data ?? null))
    }, [clientId, session])

    const respond = async (accept: boolean) => {
        setSubmitting(accept ? 'accept' : 'deny')
        const { data, error } = await authClient.$fetch<{ url?: string }>('/oauth2/consent', {
            method: 'POST',
            body: { accept, oauth_query: oauthQuery },
        })

        if (error || !data?.url) {
            toast.error(error?.message || 'This request has expired. Start the connection again from your application.')
            setSubmitting(null)
            return
        }

        window.location.href = data.url
    }

    if (!oauthQuery || !clientId) {
        return (
            <ConsentLayout>
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">Invalid request</h1>
                <p className="text-sm text-muted-foreground mt-1.5">
                    This link is missing authorization details. Start the connection again from your application.
                </p>
            </ConsentLayout>
        )
    }

    if (isPending || !session?.user) {
        return <ConsentLayout><p className="text-sm text-muted-foreground">Loading...</p></ConsentLayout>
    }

    const user = session.user as typeof session.user & { role?: string }
    const appName = client?.client_name || 'An application'

    return (
        <ConsentLayout>
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                    Connect {appName} to Trakkrr?
                </h1>
                <p className="text-sm text-muted-foreground mt-1.5">
                    Signed in as <span className="font-medium text-foreground">{user.email}</span>
                </p>
            </div>

            <div className="rounded-md border border-input bg-card p-4 space-y-3 text-sm">
                <p className="text-foreground">
                    {appName} will be able to view and create businesses, devices and models{' '}
                    <span className="font-medium">on your behalf</span>
                    {user.role && roleDescriptions[user.role] ? `, ${roleDescriptions[user.role]}` : ''}. It cannot do
                    anything your account cannot, and changes it makes are recorded in the activity log as yours.
                </p>
                {scopes.length > 0 && (
                    <ul className="space-y-1 text-muted-foreground list-disc pl-5">
                        {scopes.map((scope) => (
                            <li key={scope}>{scopeDescriptions[scope] ?? scope}</li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="mt-4 flex gap-2 rounded-md border border-input p-3 text-xs text-muted-foreground">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                <div className="space-y-1">
                    <p>
                        Only continue if you started this connection yourself. Access will be sent to{' '}
                        <span className="font-medium text-foreground">{redirectHost ?? 'an unknown address'}</span>.
                    </p>
                    {client?.client_uri && (
                        <a
                            href={client.client_uri}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                            {client.client_uri} <ExternalLink className="h-3 w-3" />
                        </a>
                    )}
                </div>
            </div>

            <div className="mt-6 flex gap-3">
                <button
                    type="button"
                    onClick={() => respond(false)}
                    disabled={submitting !== null}
                    className="h-10 flex-1 rounded-md border border-input bg-card text-sm font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                >
                    {submitting === 'deny' ? 'Cancelling...' : 'Cancel'}
                </button>
                <button
                    type="button"
                    onClick={() => respond(true)}
                    disabled={submitting !== null}
                    className="h-10 flex-1 rounded-md bg-foreground text-background text-sm font-medium hover:bg-foreground/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                >
                    {submitting === 'accept' ? 'Connecting...' : 'Allow'}
                </button>
            </div>

            <p className="mt-6 text-center text-xs text-muted-foreground">
                Not you?{' '}
                <a href="/logout" className="hover:text-foreground transition-colors underline">
                    Sign out
                </a>
            </p>
        </ConsentLayout>
    )
}

const ConsentLayout = ({ children }: { children: React.ReactNode }) => (
    <div className="flex min-h-screen">
        <div className="hidden lg:flex lg:w-1/2 bg-foreground items-center justify-center p-12">
            <div className="max-w-md">
                <h2 className="text-3xl font-semibold tracking-tight text-background">Trakkrr</h2>
                <p className="text-background/60 mt-3 text-sm leading-relaxed">
                    Connect AI assistants and other tools to your fleet. You stay in control of what they can access.
                </p>
            </div>
        </div>
        <div className="flex flex-1 flex-col justify-center px-6 sm:px-12 lg:px-16 xl:px-24 bg-background">
            <div className="w-full max-w-sm mx-auto">{children}</div>
        </div>
    </div>
)
